import { state } from '../state';
import type { VehicleData } from '../api/types';
import { formatTimeDiff } from '../utils/time';
import { showFaultDetails } from './fault-modal';
import { buildVehicleFaultCardHTML, buildTelematicsFaultCardHTML } from './fault-cards';
import { escapeHtml } from '../utils/html';
import { formatDistance, formatSpeed } from '../utils/units';

// ── Module state ───────────────────────────────────────────────────────────────
let _current: VehicleData | null = null;
let _selectedBubble: HTMLElement | null = null;
let _lastRenderKey = '';
let _vehFaultsOpen = true;
let _devNotifsOpen = false;
let _initialized   = false;

// ── Public API ─────────────────────────────────────────────────────────────────

export function initVehicleDetail(): void {
  if (_initialized) return;
  _initialized = true;

  // Modal close button
  document.getElementById('vdSheetClose')?.addEventListener('click', closeVehicleModal);

  // Click on the dimmed overlay (outside the panel) closes the modal
  document.getElementById('vehicleDetailModal')?.addEventListener('click', (e) => {
    if (e.target === e.currentTarget) closeVehicleModal();
  });

  // Delegated interactions inside the panel body (body re-renders so use delegation)
  document.getElementById('vehicleDetailPanel')?.addEventListener('click', (e) => {
    const target = e.target as Element;

    // Collapse toggles
    const collapseBtn = target.closest<HTMLElement>('.vh-collapse');
    if (collapseBtn) {
      const targetId = collapseBtn.dataset.target;
      if (targetId === 'vdVehFaults') {
        _vehFaultsOpen = !_vehFaultsOpen;
        syncCollapse('vdVehFaults', 'vdVehFaultsBtn', _vehFaultsOpen);
      } else if (targetId === 'vdDevNotifs') {
        _devNotifsOpen = !_devNotifsOpen;
        syncCollapse('vdDevNotifs', 'vdDevNotifsBtn', _devNotifsOpen);
      }
      return;
    }

    // Open full fault modal
    if (target.closest('#vdOpenFaultModal') && _current) {
      showFaultDetails(_current);
    }
  });

  // Escape: close the fault modal first (it stacks on top), then the vehicle modal
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    const faultModal = document.getElementById('faultModal');
    if (faultModal?.classList.contains('show')) {
      faultModal.classList.remove('show');
      e.preventDefault();
      return;
    }
    const overlay = document.getElementById('vehicleDetailModal');
    if (overlay?.classList.contains('show')) {
      closeVehicleModal();
      e.preventDefault();
    }
  });
}

/** Open the detail modal for a vehicle (called from a card click). */
export function selectVehicle(id: string): void {
  state.selectedVehicleId = id;

  // Highlight the clicked card (2 node ops — no grid rebuild)
  _selectedBubble?.classList.remove('hero-selected');
  _selectedBubble?.removeAttribute('aria-selected');
  const grid = document.getElementById('vehicleGrid');
  const next = findBubbleById(grid, id);
  next?.classList.add('hero-selected');
  next?.setAttribute('aria-selected', 'true');
  _selectedBubble = next;

  const vehicle = state.allVehicles.find((v) => v.device.id === id) ?? null;
  _current = vehicle;

  // Reset collapse state when changing vehicle
  _vehFaultsOpen = true;
  _devNotifsOpen = false;

  renderDetailPanel();
  openVehicleModal();
}

/** Called at the end of every displayVehicles pass (filter, refresh, unit change).
 *  Only does work when the modal is open — keeps it live without auto-opening anything. */
export function syncSelection(vehicles: VehicleData[]): void {
  const selId   = state.selectedVehicleId;
  const overlay = document.getElementById('vehicleDetailModal');
  if (!selId || !overlay?.classList.contains('show')) return;

  // If the open vehicle dropped out of the fleet, close the modal
  if (!vehicles.find((v) => v.device.id === selId)) {
    closeVehicleModal();
    return;
  }

  // Re-sync the highlight (grid may have rebuilt the node)
  const grid = document.getElementById('vehicleGrid');
  const bubble = findBubbleById(grid, selId);
  if (_selectedBubble !== bubble) {
    _selectedBubble?.classList.remove('hero-selected');
    _selectedBubble?.removeAttribute('aria-selected');
    bubble?.classList.add('hero-selected');
    bubble?.setAttribute('aria-selected', 'true');
    _selectedBubble = bubble;
  }

  // Re-render panel only when something relevant changed
  const vehicle = state.allVehicles.find((v) => v.device.id === selId) ?? null;
  _current = vehicle;
  const key = `${state.dataVersion}|${selId}|${state.odometerUnit}|${state.visibleFields.join()}`;
  if (key === _lastRenderKey) return;

  // Preserve scroll and collapse state during same-vehicle data refresh
  const body = document.getElementById('vdBody');
  const scrollTop = body?.scrollTop ?? 0;
  renderDetailPanel();
  if (body) body.scrollTop = scrollTop;
}

/** Update the fleet summary strip (total, nominal pct, status bar, per-status chips). */
export function updateFleetSummary(): void {
  const vehicles = state.allVehicles;
  if (!vehicles.length) return;

  const total    = vehicles.length;
  const healthy  = vehicles.filter((v) => v.healthStatus === 'healthy').length;
  const warning  = vehicles.filter((v) => v.healthStatus === 'warning').length;
  const critical = vehicles.filter((v) => v.healthStatus === 'critical').length;
  const nominalPct = Math.round((healthy / total) * 100);

  setText('heroTotalCount', String(total));
  setText('heroNominalPct', `${nominalPct}% of fleet nominal`);

  setFlex('heroBarH', healthy);
  setFlex('heroBarW', warning);
  setFlex('heroBarC', Math.max(critical, 0.01));

  const rows = document.getElementById('heroStatusRows');
  if (!rows) return;
  rows.innerHTML = [
    { label: 'Healthy',  count: healthy,  cls: 'h' },
    { label: 'Warning',  count: warning,  cls: 'w' },
    { label: 'Critical', count: critical, cls: 'c' },
  ].map(({ label, count, cls }) =>
    `<span class="vh-chip ${cls}">
      <span class="vh-chip-dot ${cls}"></span>
      <span class="vh-chip-lbl">${label}</span>
      <span class="vh-chip-n">${count}</span>
      <span class="vh-chip-pct">${pct(count, total)}%</span>
    </span>`
  ).join('');
}

// ── Rendering ──────────────────────────────────────────────────────────────────

function renderDetailPanel(): void {
  const v = _current;

  // Update banner status tint
  const banner = document.getElementById('vdBanner');
  if (banner) {
    banner.className = `vh-banner status-${v ? v.healthStatus : 'healthy'}`;
  }

  // Banner header fields (always visible)
  setText('vdName', v ? v.device.name : '—');

  const badge = document.getElementById('vdBadge');
  if (badge) {
    badge.textContent = v ? v.healthStatus.toUpperCase() : '—';
    badge.className   = `hero-status-badge ${v ? v.healthStatus : 'healthy'}`;
  }

  if (v) {
    const lastCommStr = v.lastCommunication
      ? `${formatTimestamp(v.lastCommunication)} · LIVE`
      : '—';
    setText('vdLastSeen', lastCommStr);
  } else {
    setText('vdLastSeen', '—');
  }

  // Render detail body
  const body = document.getElementById('vdBody');
  if (!body) return;

  if (!v) {
    body.innerHTML = '<div class="vh-empty">Select a vehicle to view details.</div>';
    _lastRenderKey = '';
    return;
  }

  const key = `${state.dataVersion}|${v.device.id}|${state.odometerUnit}|${state.visibleFields.join()}`;
  _lastRenderKey = key;

  body.innerHTML = buildDetailBodyHTML(v);

  // Restore collapse states
  syncCollapse('vdVehFaults', 'vdVehFaultsBtn', _vehFaultsOpen);
  syncCollapse('vdDevNotifs', 'vdDevNotifsBtn', _devNotifsOpen);
}

function buildDetailBodyHTML(v: VehicleData): string {
  const vf = v.faults.filter((f) => f.source === 'vehicle')
    .sort((a, b) => new Date(b.dateTime).getTime() - new Date(a.dateTime).getTime());
  const tf = v.faults.filter((f) => f.source === 'telematics')
    .sort((a, b) => new Date(b.dateTime).getTime() - new Date(a.dateTime).getTime());

  const { odometerUnit, visibleFields: vf2 } = state;

  // ── Header stats section (always visible) ─────────────────────────────────
  const engineVal = v.engineRunning ? 'Running' : 'Off';
  const vfCount   = v.vehicleFaultCount;
  const tfCount   = v.faults.filter((f) => f.source === 'telematics').length;
  const devNote   = tfCount > 0 ? ` <span class="vh-row-muted">· ${tfCount} device notification${tfCount > 1 ? 's' : ''}</span>` : '';

  let html = `
    <div class="vh-header-stats">
      <div class="vh-hstat">
        <div class="vh-hstat-k">Engine</div>
        <div class="vh-hstat-v ${v.engineRunning ? 'running' : 'off'}">${engineVal}</div>
      </div>
      <div class="vh-hstat">
        <div class="vh-hstat-k">Vehicle Faults</div>
        <div class="vh-hstat-v ${vfCount > 0 ? (vfCount > 5 ? 'critical' : 'warning') : 'healthy'}">${vfCount}${devNote}</div>
      </div>
    </div>`;

  // ── Operational section ───────────────────────────────────────────────────
  const opsFields: Array<{ key: string; label: string; value: string }> = [];
  if (vf2.includes('speed'))
    opsFields.push({ key: 'speed', label: 'Speed', value: formatSpeed(v.speed, odometerUnit) });
  if (vf2.includes('fuelLevel'))
    opsFields.push({ key: 'fuelLevel', label: 'Fuel Level', value: v.fuelLevel !== null ? `${v.fuelLevel}%` : '—' });
  if (vf2.includes('battery'))
    opsFields.push({ key: 'battery', label: 'Battery', value: v.battery !== null ? `${v.battery}V` : '—' });
  if (vf2.includes('odometer')) {
    const odoVal = v.odometer !== null
      ? formatDistance(v.odometer, { unit: odometerUnit })
      : '—';
    opsFields.push({ key: 'odometer', label: 'Odometer', value: odoVal });
  }

  if (opsFields.length > 0) {
    html += `<div class="vh-section">
      <div class="vh-section-title">Operational</div>`;
    if (vf2.includes('fuelLevel') && v.fuelLevel !== null) {
      html += `<div class="vh-fuel-row">
        <span class="vh-row-k">Fuel Level</span>
        <div class="vh-fuel-track"><div class="hero-fuel-fill" style="width:${v.fuelLevel}%"></div></div>
        <span class="vh-row-v mono">${v.fuelLevel}%</span>
      </div>`;
    }
    opsFields.filter((f) => f.key !== 'fuelLevel').forEach((f) => {
      html += `<div class="vh-row"><span class="vh-row-k">${escapeHtml(f.label)}</span><span class="vh-row-v mono">${escapeHtml(f.value)}</span></div>`;
    });
    html += `</div>`;
  }

  // ── Assignment & ID section ────────────────────────────────────────────────
  const idFields: Array<{ key: string; label: string; value: string }> = [];
  if (vf2.includes('driver'))
    idFields.push({ key: 'driver', label: 'Driver', value: v.driver });
  if (vf2.includes('group'))
    idFields.push({ key: 'group', label: 'Group / Division', value: v.group });
  if (vf2.includes('vin'))
    idFields.push({ key: 'vin', label: 'VIN', value: v.vin === 'N/A' ? '—' : v.vin });
  if (vf2.includes('serialNumber'))
    idFields.push({ key: 'serialNumber', label: 'Serial Number', value: v.serialNumber });
  if (vf2.includes('type'))
    idFields.push({ key: 'type', label: 'Device Type', value: v.device.deviceType ?? 'Vehicle' });
  if (vf2.includes('lastCommunication'))
    idFields.push({ key: 'lastCommunication', label: 'Last Seen', value: v.lastCommunication ? formatTimeDiff(v.lastCommunication) : '—' });

  if (idFields.length > 0) {
    html += `<div class="vh-section">
      <div class="vh-section-title">Assignment &amp; ID</div>`;
    idFields.forEach((f) => {
      html += `<div class="vh-row"><span class="vh-row-k">${escapeHtml(f.label)}</span><span class="vh-row-v mono">${escapeHtml(f.value)}</span></div>`;
    });
    html += `</div>`;
  }

  // ── Diagnostics section ────────────────────────────────────────────────────
  html += `<div class="vh-section vh-diagnostics">
    <div class="vh-section-title">Diagnostics</div>`;

  // Vehicle faults subsection
  const vfTriggerTotal = vf.reduce((s, f) => s + (f.count ?? 1), 0);
  const vehHeader = vf.length > 0
    ? `<span class="vh-collapse-label critical">Vehicle Faults (${vf.length} code${vf.length > 1 ? 's' : ''} · ${vfTriggerTotal} triggered) — Action Required</span>`
    : `<span class="vh-collapse-label healthy">Vehicle Faults — None</span>`;
  html += `<button class="vh-collapse" id="vdVehFaultsBtn" data-target="vdVehFaults" aria-expanded="true">${vehHeader}<span class="vh-caret">▾</span></button>
    <div class="vh-collapse-body" id="vdVehFaults">`;

  if (vf.length === 0) {
    html += `<div class="vh-no-faults">✓ No vehicle faults — all systems nominal</div>`;
  } else {
    const cap = 6;
    vf.slice(0, cap).forEach((fault) => {
      html += buildVehicleFaultCardHTML(fault);
    });
    if (vf.length > cap) {
      html += `<div class="vh-more-faults">+ ${vf.length - cap} more fault${vf.length - cap > 1 ? 's' : ''} — open full view below</div>`;
    }
  }
  html += `</div>`;

  // Device notifications subsection
  html += `<button class="vh-collapse" id="vdDevNotifsBtn" data-target="vdDevNotifs" aria-expanded="false">
    <span class="vh-collapse-label muted">Device Notifications (${tf.length}) — Informational</span>
    <span class="vh-caret">▸</span>
  </button>
  <div class="vh-collapse-body hidden" id="vdDevNotifs">`;

  if (tf.length === 0) {
    html += `<div class="vh-no-faults muted">No device notifications.</div>`;
  } else {
    const tfCap = 10;
    tf.slice(0, tfCap).forEach((fault) => {
      html += buildTelematicsFaultCardHTML(fault);
    });
    if (tf.length > tfCap) {
      html += `<div class="vh-more-faults">+ ${tf.length - tfCap} more — open full view below</div>`;
    }
  }
  html += `</div>`;

  // Full fault view button
  html += `<button class="hero-all-btn vh-fault-btn" id="vdOpenFaultModal">Open full fault view ↗</button>`;
  html += `</div>`;

  return html;
}

// ── Modal helpers ────────────────────────────────────────────────────────────

function openVehicleModal(): void {
  document.getElementById('vehicleDetailModal')?.classList.add('show');
  document.body.classList.add('vd-modal-open');
  if (document.body.classList.contains('addin-mode')) {
    document.getElementById('mainContent')?.classList.add('vd-modal-overflow-hidden');
  }
  // Move focus into the dialog for keyboard / screen-reader users
  document.getElementById('vdSheetClose')?.focus();
}

function closeVehicleModal(): void {
  document.getElementById('vehicleDetailModal')?.classList.remove('show');
  document.body.classList.remove('vd-modal-open');
  document.getElementById('mainContent')?.classList.remove('vd-modal-overflow-hidden');

  // Clear the grid highlight so nothing looks "selected" when the modal is closed
  _selectedBubble?.classList.remove('hero-selected');
  _selectedBubble?.removeAttribute('aria-selected');
  _selectedBubble = null;
  state.selectedVehicleId = null;
  _current = null;
}

function syncCollapse(bodyId: string, btnId: string, open: boolean): void {
  const body = document.getElementById(bodyId);
  const btn  = document.getElementById(btnId);
  if (body) body.classList.toggle('hidden', !open);
  if (btn) {
    btn.setAttribute('aria-expanded', String(open));
    const caret = btn.querySelector<HTMLElement>('.vh-caret');
    if (caret) caret.textContent = open ? '▾' : '▸';
  }
}

// ── Helpers ────────────────────────────────────────────────────────────────────

function setText(id: string, value: string): void {
  const el = document.getElementById(id);
  if (el) el.textContent = value;
}

function findBubbleById(grid: HTMLElement | null, id: string): HTMLElement | null {
  if (!grid) return null;
  return Array.from(grid.querySelectorAll<HTMLElement>('.vbubble[data-id]'))
    .find((bubble) => bubble.dataset.id === id) ?? null;
}

function setFlex(id: string, value: number): void {
  const el = document.getElementById(id) as HTMLElement | null;
  if (el) el.style.flex = String(value);
}

function pct(n: number, total: number): number {
  return total > 0 ? Math.round((n / total) * 100) : 0;
}

function formatTimestamp(date: Date): string {
  const d  = String(date.getDate()).padStart(2, '0');
  const m  = String(date.getMonth() + 1).padStart(2, '0');
  const y  = date.getFullYear();
  const hh = String(date.getHours()).padStart(2, '0');
  const mm = String(date.getMinutes()).padStart(2, '0');
  return `${d}.${m}.${y} · ${hh}:${mm}`;
}
